import React, { useEffect, useState } from 'react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import {
  MATERIAL_LIST_ENDPOINT,
  GET_LOADING_POINTS_ENDPOINT,
  SAVE_PRODUCTION_ENDPOINT
} from '../../config/api';
import { decryptData } from '../../utils/encrypt';

function ProductionNew() {
  const [materials, setMaterials] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [packingType, setPackingType] = useState('');
  const [plant, setPlant] = useState('');
  const [productionDate, setProductionDate] = useState('');
  const [quantity, setQuantity] = useState('');
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [saving, setSaving] = useState(false);

  const packingOptions = ['25 Kgs', '50 Kgs', 'Loose Bulker'];

  useEffect(() => {
    getUserDetails();
  }, []);

  useEffect(() => {
    if (orgId) {
      fetchMaterialList();
      fetchLoadingPoints();
      setDefaultDate();
    }
  }, [orgId]);

  const getUserDetails = () => {
    const stored = localStorage.getItem('suyog_user');
    if (stored) {
      const parsed = decryptData(stored);
      const uid = parsed?.user_id || null;
      const oid = parsed?.selected_org?.value || null;
      setUserId(uid);
      setOrgId(oid);
    }
  };

  const fetchMaterialList = async () => {
    try {
      const res = await fetch(MATERIAL_LIST_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setMaterials(data);
      } else {
        console.error('Invalid material list:', data);
      }
    } catch (err) {
      console.error('Error fetching materials:', err);
    }
  };

  const fetchLoadingPoints = async () => {
    try {
      const res = await fetch(GET_LOADING_POINTS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: orgId })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setLoadingPoints(data.records || []);
      } else {
        console.error('Failed to load loading points:', data);
      }
    } catch (err) {
      console.error('Error fetching loading points:', err);
    }
  };

  const setDefaultDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const formatted = yesterday.toISOString().split('T')[0];
    setProductionDate(formatted);
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    const valid = /^\d*\.?\d{0,3}$/.test(value);
    if (valid) {
      setQuantity(value);
    }
  };

  const handleSave = async () => {
    if (!userId || !orgId || !selectedMaterialId || !packingType || !plant || !productionDate || quantity === '') {
      alert('Please fill in all fields.');
      return;
    }

    const payload = {
      user_id: userId,
      org_id: orgId,
      material_id: selectedMaterialId,
      packing_type: packingType,
      plant_name: plant,
      production_date: productionDate,
      quantity: parseFloat(quantity)
    };

    setSaving(true);
    try {
      const res = await fetch(SAVE_PRODUCTION_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (res.ok && data.success) {
        alert('Production record saved successfully!');
        setSelectedMaterialId('');
        setPackingType('');
        setPlant('');
        setQuantity('');
        setDefaultDate();
      } else {
        alert('Failed to save. Please try again.');
        console.error(data);
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h2 className="mb-4">Production Entry</h2>

        <div className="card shadow rounded-4">
          <div className="card-header fw-bold fs-5">Production Details</div>
          <div className="card-body">
            <form className="row g-4">

              {/* Production Date */}
              <div className="col-md-4">
                <label htmlFor="date" className="form-label">Production Date</label>
                <input
                  type="date"
                  className="form-control"
                  id="date"
                  value={productionDate}
                  onChange={(e) => setProductionDate(e.target.value)}
                />
              </div>

              {/* Material Dropdown */}
              <div className="col-md-4">
                <label htmlFor="material" className="form-label">Material</label>
                <select
                  id="material"
                  className="form-select"
                  value={selectedMaterialId}
                  onChange={(e) => setSelectedMaterialId(e.target.value)}
                >
                  <option value="">-- Select Material --</option>
                  {materials.map((material) => (
                    <option key={material.id} value={material.id}>
                      {material.material_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Packing Type Dropdown */}
              <div className="col-md-4">
                <label htmlFor="packing" className="form-label">Packing Type</label>
                <select
                  id="packing"
                  className="form-select"
                  value={packingType}
                  onChange={(e) => setPackingType(e.target.value)}
                >
                  <option value="">-- Select Packing Type --</option>
                  {packingOptions.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Loading Point Dropdown */}
              <div className="col-md-4">
                <label htmlFor="plant" className="form-label">Loading Point</label>
                <select
                  id="plant"
                  className="form-select"
                  value={plant}
                  onChange={(e) => setPlant(e.target.value)}
                >
                  <option value="">-- Select Loading Point --</option>
                  {loadingPoints.map((lp) => (
                    <option key={lp.id} value={lp.name}>{lp.name}</option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div className="col-md-4">
                <label htmlFor="quantity" className="form-label">Quantity - MTS</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  id="quantity"
                  placeholder="Enter quantity"
                  value={quantity}
                  onChange={handleQuantityChange}
                  autoComplete="off"
                />
              </div>

              {/* Save Button */}
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default ProductionNew;
